import { useContext, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import {
  ScannerInput,
  InputText,
  TableSimple,
  TriangleAlertIcon,
  CustomScrollbarV2,
  Span,
  InputCheckbox,
  FlexContainer,
  useNotificationsV2,
  SoundNotificationContext,
  Button,
  Loader,
} from '@uni-space/uni-components';
import './ConsolidatorTracking.scss';
import {
  ErrorRow,
  ParcelAcceptanceRequest,
  ParcelDimensions,
  StatusAcceptanceEnum,
  SuccessRow,
} from '../model/consolidator';
import { useConsolidatorTableSettings } from '../model/useConsolidatorTableSettings';
import { getWeight, uploadPhoto } from '../api';
import { useTranslation } from 'react-i18next';
import { useFetchSendData } from '../model/useFetchSendData';
import ChangeWeightModal from './ChangeWeightModal';
import ChangeDimensionsModal from './ChangeDimensionsModal';
import ParcelDimensionsModal from './ParcelDimensionsModal';

export default function ConsolidatorTracking() {
  const [isFocus, setIsFocus] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [weightDraft, setWeightDraft] = useState('');

  const webcamRef = useRef<Webcam | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const { onSoundError } = useContext(SoundNotificationContext);
  const [isOpenChangeWeight, setIsOpenChangeWeight] = useState<boolean>(false);
  const [isOpenChangeDimensions, setIsOpenChangeDimensions] = useState<boolean>(false);

  const [successRows, setSuccessRows] = useState<SuccessRow[]>([]);
  const [errorRows, setErrorRows] = useState<ErrorRow[]>([]);
  const [connection, setConnection] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);
  const scanLocked = useRef(false);
  const submissionLocked = useRef(false);
  const [pendingParcel, setPendingParcel] = useState<
    (Omit<ParcelAcceptanceRequest, 'dimensions'> & { shot: string }) | null
  >(null);

  const { errorColumns, successColumns } = useConsolidatorTableSettings();
  const { t, i18n } = useTranslation();
  const [isLoadingScaleConnection, setIsLoadingScaleConnection] = useState<boolean>(true);

  const { createNotification } = useNotificationsV2();

  const testConnection = async () => {
    try {
      const res = await getWeight();
      setConnection(Boolean(res));
    } catch (error) {
    } finally {
      setIsLoadingScaleConnection(false);
    }
  };

  useEffect(() => {
    testConnection();
  }, []);

  const addErrorRow = (error: ErrorRow) => {
    setErrorRows((prev) => [error, ...prev]);
  };

  const addSuccessRow = (success: SuccessRow) => {
    setSuccessRows((prev) => [success, ...prev]);
  };

  const changeWeightSuccessList = (weight: number, trackNumber: string) => {
    setSuccessRows((prev) => prev.map((i) => (i.trackNumber === trackNumber ? { ...i, weight } : i)));
  };

  const { fetchSendData } = useFetchSendData({ addErrorRow, addSuccessRow, setIsLoading });

  const setManualEnabled = (next: boolean) => {
    setManualOpen(next);

    if (!next) {
      setWeightDraft('');
      setIsFocus(false);
    }
  };

  const parseManualWeight = (s: string) => {
    const v = Number.parseInt(s, 10);
    return Number.isFinite(v) ? v : null;
  };

  const onScanSubmit = async (trackNumber: string) => {
    if (scanLocked.current) return;
    scanLocked.current = true;
    setIsLoading(true);
    let prepared = false;

    try {
      let shot = null;
      shot = webcamRef.current?.getScreenshot() ?? '';
      const weightResponse = await getWeight();
      const scalesWeight = weightResponse?.weight ?? null;

      if (!scalesWeight) {
        setConnection(false);
        return;
      }

      const manualWeight = manualOpen && weightDraft ? parseManualWeight(weightDraft) : null;
      const actualWeight = manualWeight ?? scalesWeight;

      if (!actualWeight) return;

      if (actualWeight < 0 || actualWeight >= 30000) {
        createNotification({
          type: 'error',
          message: { title: t('weightError') },
        });
        onSoundError();
        return;
      }

      setPendingParcel({
        trackNumber,
        actualWeight,
        factWeight: scalesWeight,
        shot,
      });
      prepared = true;
    } finally {
      if (!prepared) scanLocked.current = false;
      setIsLoading(false);
    }
  };

  const clearPendingParcel = () => {
    if (submissionLocked.current) return;
    setPendingParcel(null);
    scanLocked.current = false;
  };

  const onDimensionsSubmit = async (dimensions: ParcelDimensions) => {
    if (!pendingParcel || submissionLocked.current) return;
    submissionLocked.current = true;
    setIsLoading(true);
    const { shot, ...parcel } = pendingParcel;
    try {
      const response = await fetchSendData({ ...parcel, dimensions });
      if (response.status === StatusAcceptanceEnum.InvalidDimensions) return;
      submissionLocked.current = false;
      clearPendingParcel();
      setWeightDraft('');

      // Фото отправляем отдельно после acceptance, не блокируя следующий скан.
      // Вместе с ним — учтённый вес и габариты: сервис весов подпишет их на кадре.
      if (shot) {
        uploadPhoto(parcel.trackNumber, shot, { weight: parcel.actualWeight, dimensions }).catch(() => {
          createNotification({
            type: 'error',
            message: { title: t('consolidatorTracking.scan.photoUploadError') },
          });
        });
      }
    } catch {
      // The mutation displays the API error. Keep dimensions available for retry.
    } finally {
      submissionLocked.current = false;
      setIsLoading(false);
    }
  };

  if (isLoadingScaleConnection) {
    return (
      <FlexContainer gap="16px" align="center">
        <Loader size="s" />
        <Span fw={500} fs={16}>
          {t('tryConnectToScales')}
        </Span>
      </FlexContainer>
    );
  }

  if (!connection) {
    return (
      <FlexContainer direction="column" gap="16px">
        <Span fs={23}>{t('scalesNotConnected')}</Span>
        <Span fs={23}>{t('reloadPage')}</Span>
      </FlexContainer>
    );
  }

  return (
    <CustomScrollbarV2 height={'calc(100vh - 100px)'}>
      {pendingParcel && (
        <ParcelDimensionsModal
          trackNumber={pendingParcel.trackNumber}
          weight={pendingParcel.actualWeight}
          isLoading={isLoading}
          onCancel={clearPendingParcel}
          onSubmit={onDimensionsSubmit}
        />
      )}
      <ChangeWeightModal
        isOpenChangeWeight={isOpenChangeWeight}
        onChangeOpen={(isOpen) => setIsOpenChangeWeight(isOpen)}
        changeWeightSuccessList={changeWeightSuccessList}
      />
      <ChangeDimensionsModal isOpen={isOpenChangeDimensions} onChangeOpen={setIsOpenChangeDimensions} />

      <div className="ct6-page">
        <div className="ct6-topCard">
          <div className="ct6-topGrid">
            <div className="ct6-left">
              <FlexContainer gap="8px" style={{ flexWrap: 'wrap' }}>
                <Button width="180px" onClick={() => setIsOpenChangeWeight(true)}>
                  {t('changeWeightTitle')}
                </Button>
                <Button width="180px" onClick={() => setIsOpenChangeDimensions(true)}>
                  {t('consolidatorTracking.changeDimensions.title')}
                </Button>
              </FlexContainer>

              <div className="ct6-scan">
                <ScannerInput
                  label={t('consolidatorTracking.scan.trackNumberLabel')}
                  buttonLabel={t('consolidatorTracking.scan.scanButton')}
                  isLoading={isLoading || Boolean(pendingParcel)}
                  disabled={isLoading || Boolean(pendingParcel)}
                  maxValueLength={64}
                  lang={i18n.language}
                  isFocusFilter={
                    Boolean(pendingParcel) || (manualOpen && isFocus) || isOpenChangeWeight || isOpenChangeDimensions
                  }
                  onSubmit={onScanSubmit}
                />
                <div className="ct6-hint">{t('consolidatorTracking.scan.photoHint')}</div>
              </div>

              <div className="ct6-manual">
                <div className="ct6-manualHead">
                  <div className={`ct6-manualToggle ${manualOpen ? 'ct6-manualToggle--on' : ''}`}>
                    <div className="ct6-manualToggleLeft">
                      <div className="ct6-manualToggleTitle">{t('consolidatorTracking.manual.title')}</div>
                      <div className="ct6-manualToggleDesc">{t('consolidatorTracking.manual.desc')}</div>
                    </div>

                    <InputCheckbox
                      id="manual-weight-toggle"
                      checked={manualOpen}
                      disabled={isLoading}
                      label={manualOpen ? t('consolidatorTracking.manual.on') : t('consolidatorTracking.manual.off')}
                      onChange={(checked) => setManualEnabled(checked)}
                    />
                  </div>
                </div>

                {manualOpen && (
                  <>
                    <div className="ct6-warning">
                      <TriangleAlertIcon color="red" />
                      <span>{t('consolidatorTracking.manual.warning')}</span>
                    </div>

                    <InputText
                      id="manual-weight"
                      label={t('consolidatorTracking.manual.weightLabel')}
                      value={weightDraft}
                      type="number"
                      textAlign="left"
                      onChange={(value: string) => setWeightDraft(value)}
                      onFocus={() => setIsFocus(true)}
                      onBlur={() => setIsFocus(false)}
                    />

                    <div className="ct6-subhint">{t('consolidatorTracking.manual.subhint')}</div>
                  </>
                )}
              </div>
            </div>

            <div className="ct6-cameraCard">
              <div className="ct6-cardHead">
                <div>
                  <div className="ct6-title">{t('consolidatorTracking.camera.title')}</div>
                  <div className="ct6-muted">
                    <Span>{t('consolidatorTracking.camera.statusLabel')} </Span>

                    {cameraError ? (
                      <Span fw={600} color="red">
                        {t('consolidatorTracking.camera.errorWithReason', { reason: cameraError })}
                      </Span>
                    ) : cameraReady ? (
                      <Span>{t('consolidatorTracking.camera.ready')}</Span>
                    ) : (
                      <Span>{t('consolidatorTracking.camera.requesting')}</Span>
                    )}
                  </div>
                </div>
              </div>

              <div className="ct6-webcamWrap">
                {/* Снимок без сжатия: PNG без потерь и кадр в исходном разрешении камеры, а не в размере элемента на экране */}
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/png"
                  forceScreenshotSourceSize
                  videoConstraints={{
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                  }}
                  className="ct6-webcam"
                  onUserMedia={() => {
                    setCameraReady(true);
                    setCameraError(null);
                  }}
                  onUserMediaError={(e) => {
                    setCameraReady(false);
                    const reason = (e as any)?.name ?? t('consolidatorTracking.camera.permissionDenied');
                    setCameraError(reason);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="ct6-tablesGrid">
          <div className="ct6-card ct6-card--danger">
            <div className="ct6-cardHead">
              <div className="ct6-title">{t('consolidatorTracking.tables.errors.title')}</div>
              <div className="ct6-counter ct6-counterDanger">{errorRows.length}</div>
            </div>

            <TableSimple<ErrorRow>
              columns={errorColumns}
              data={errorRows}
              enableColumnResizing={false}
              withOutPagination
              isLoading={false}
              emptyPlaceholder={t('consolidatorTracking.tables.errors.empty')}
              mobileWidth={680}
              maxHeightTable="calc(100dvh - 600px)"
            />
          </div>

          <div className="ct6-card">
            <div className="ct6-cardHead">
              <div className="ct6-title">{t('consolidatorTracking.tables.success.title')}</div>
              <div className="ct6-counter">{successRows.length}</div>
            </div>

            <TableSimple<SuccessRow>
              columns={successColumns}
              data={successRows}
              enableColumnResizing={false}
              withOutPagination
              isLoading={false}
              emptyPlaceholder={t('consolidatorTracking.tables.success.empty')}
              mobileWidth={680}
              maxHeightTable="calc(100dvh - 600px)"
            />
          </div>
        </div>
      </div>
    </CustomScrollbarV2>
  );
}
